import type { IReservationTableRef } from './IReservation';

export type TTableReservationsMode = 'online' | 'internal' | 'none';
export type TReservationDepositMode = 'none' | 'threshold' | 'always';

export interface IReservationDurationRule {
  maxParty: number;
  minutes: number;
}

/** Minutes between the arrival times of a shift without fixed times (same set as the API). */
export const RESERVATION_SLOT_INTERVALS = [15, 30, 45, 60, 90, 120] as const;
export type TReservationSlotInterval = typeof RESERVATION_SLOT_INTERVALS[number];
/** Grid step of a new shift until its owner picks one (same default as the API). */
export const RESERVATION_DEFAULT_SLOT_INTERVAL_MIN: TReservationSlotInterval = 30;
export function isReservationSlotInterval(
  value: unknown
): value is TReservationSlotInterval {
  return RESERVATION_SLOT_INTERVALS.some((interval) => interval === value);
}

/** One fixed arrival time of a shift or special-date window. */
export interface IReservationArrivalTime {
  /** Branch-local HH:mm; a time earlier than the window start belongs to the next date. */
  time: string;
  /** Covers accepted at this time; null = only the general per-slot limit applies. */
  maxCovers: number | null;
}

/** Stored settings returned by PUT /settings. Capability flags only occur in the GET bundle. */
export interface IReservationSettings {
  id: number;
  restaurantId: number;
  branchId: number;
  enabled: boolean;
  onlineEnabled: boolean;
  defaultDurationMin: number;
  durationRules: IReservationDurationRule[];
  bufferMin: number;
  minPartyOnline: number;
  maxPartyOnline: number;
  minLeadMin: number;
  maxAdvanceDays: number;
  autoConfirm: boolean;
  toleranceMin: number;
  pacingMaxCoversPerSlot: number | null;
  depositMode: TReservationDepositMode;
  depositPartyThreshold: number;
  depositAmountPerPerson: number;
  depositRefundHours: number;
  policyText: string | null;
  remindersEnabled: boolean;
  whatsappEnabled: boolean;
  staffEmail: string | null;
  notifyMeEnabled: boolean;
  reminderHoursBefore: number;
  createdAt: string;
  updatedAt: string;
}

export interface IReservationShift {
  id: number;
  restaurantId: number;
  branchId: number;
  name: string;
  /** Sunday = 0 through Saturday = 6; no Globals.days sentinel 7. */
  days: number[];
  /** Branch-local HH:mm; to < from ends on the next calendar date. */
  from: string;
  to: string;
  maxCovers: number | null;
  /** Sorted fixed arrival times replacing the interval grid; null = grid. */
  arrivalTimes: IReservationArrivalTime[] | null;
  /** Minutes between the arrival times of a grid shift. */
  slotIntervalMin: TReservationSlotInterval;
  /** null inherits the branch deposit rule. */
  depositRequired: boolean | null;
  sort: number;
  createdAt: string;
  updatedAt: string;
}

export interface IReservationOverrideWindow {
  from: string;
  to: string;
  maxCovers: number | null;
  arrivalTimes?: IReservationArrivalTime[] | null;
}

export interface IReservationDateOverride {
  id: number;
  restaurantId: number;
  branchId: number;
  date: string;
  closed: boolean;
  onlineClosed: boolean;
  depositRequired: boolean | null;
  windows: IReservationOverrideWindow[] | null;
  /** Grid step of the custom windows; null = as the regular shifts. Older APIs omit it. */
  slotIntervalMin?: TReservationSlotInterval | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IReservationTableGroup {
  id: number;
  restaurantId: number;
  branchId: number;
  name: string;
  tableIds: number[];
  minParty: number;
  maxParty: number;
  createdAt: string;
  updatedAt: string;
}

/** Read-only flags that accompany the GET bundle; never sent back on PUT. */
export interface IReservationSettingsCapabilities {
  whatsappAvailable: boolean;
  mpConnected: boolean;
  /** MercadoPago (the only deposit provider) exists in the restaurant's country. */
  depositsAvailable: boolean;
}

export interface IReservationSettingsConfiguration {
  settings: IReservationSettings & IReservationSettingsCapabilities;
  shifts: IReservationShift[];
  /** Initial GET bundle includes today through 90 days ahead. */
  overrides: IReservationDateOverride[];
  tableGroups: IReservationTableGroup[];
  tables: IReservationTableRef[];
}

/** Operational read for ReservationsManage; excludes administrative settings. */
export interface IReservationBookingContext {
  settings: Pick<
    IReservationSettings,
    'defaultDurationMin' | 'durationRules' | 'bufferMin'
  >;
  tableGroups: Pick<
    IReservationTableGroup,
    'id' | 'tableIds' | 'minParty' | 'maxParty'
  >[];
}
